import React from 'react';
import { XCircle, AlertTriangle, Search, MousePointerClick, PhoneOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
const painIcons = [Search, MousePointerClick, PhoneOff];
export function ProblemSection() {
  const {
    t
  } = useLang();
  return <section className="py-24 bg-dark relative overflow-hidden" data-id="element-434" data-mp="2vppkb07ijs92">
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-red-900/10 via-dark to-dark pointer-events-none" data-id="element-435" data-mp="39hctg0mk0v99"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-436" data-mp="3jg23j787lbey">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-437" data-mp="3jdza0d7ul05h">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-438" data-mp="3eia9a6k3dd7l">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-439" data-mp="oc7ut93arzf4">
              {t.problem.titleA} <br data-id="element-440" data-mp="se1ube1raxdp" />
              <span className="text-red-500" data-id="element-441" data-mp="sdjn5mdk2yr0">{t.problem.titleHighlight}</span>
            </h2>

            <div className="space-y-4 text-lg text-gray-400 border-l-2 border-red-500/30 pl-6" data-id="element-442" data-mp="x51m64w0qp9c">
              <p data-id="element-443" data-mp="1244kz7lenz8v">
                {t.problem.p1a}{' '}
                <strong className="text-white" data-id="element-444" data-mp="125pd6qk8z63k">{t.problem.p1strong}</strong>
                {t.problem.p1b}
              </p>
              <p data-id="element-445" data-mp="1c226vofsljyb">
                {t.problem.p2a}{' '}
                <strong className="text-red-400" data-id="element-446" data-mp="1c2ak5hftura1">{t.problem.p2strong}</strong>
              </p>
              <p data-id="element-447" data-mp="1g2uhal0ehuw6">{t.problem.p3}</p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl" data-id="element-448" data-mp="3dtxbxl7jmq7k">
            <img src="https://cdn.magicpatterns.com/uploads/2rQBgTvyhq8rw6xqUkqADG/aselite1.png" alt={t.problem.imageAlt} className="w-full h-full object-cover" data-id="element-449" data-mp="y29tubn2f8ae" />
            {/* Before / After labels */}
            <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-red-500/90 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-id="element-450" data-mp="130thcghb4s4v">
              <XCircle className="w-3.5 h-3.5" data-id="element-451" data-mp="1cq391v88o8sg" />
              {t.problem.before}
            </span>
            <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-gold text-dark text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-id="element-452" data-mp="1qp317cuyn5td">
              {t.problem.after}
            </span>
          </motion.div>
        </div>

        {/* Pain points */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="mt-16 grid sm:grid-cols-3 gap-6" data-id="element-453" data-mp="2abeepqcv739m">
          {t.problem.painPoints.map((point, index) => {
          const Icon = painIcons[index];
          return <div key={index} className="bg-dark-100 p-6 rounded-2xl border border-white/5 flex items-center gap-5 shadow-lg" data-id="element-454" data-mp="1co5vk20c66as" data-mpi={index}>
                <div className="bg-red-500/10 p-4 rounded-full shrink-0" data-id="element-455" data-mp="1hke9jc2srtgd">
                  <Icon className="w-7 h-7 text-red-500" data-id="element-456" data-mp="13nr74ts2sb32" />
                </div>
                <div data-id="element-457" data-mp="18kr5eiioo9jm">
                  <h3 className="text-white font-bold mb-1" data-id="element-458" data-mp="18o56lnfm2cph">{point.problem}</h3>
                  <p className="text-gray-400 text-sm flex items-center gap-2" data-id="element-459" data-mp="10kuuphopkl9k">
                    <AlertTriangle className="w-4 h-4 text-gold shrink-0" data-id="element-460" data-mp="vnb55lmk8m2b" />
                    {point.cost}
                  </p>
                </div>
              </div>;
        })}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="mt-24 text-center max-w-3xl mx-auto bg-gradient-to-b from-dark-100 to-dark p-10 rounded-3xl border border-gold/20 shadow-[0_0_40px_rgba(212,175,55,0.05)]" data-id="element-461" data-mp="mt6gn8uwfmpy">
          <p className="text-gray-300 text-lg mb-4" data-id="element-462" data-mp="229x9gzwkrw1p">{t.problem.closing1}</p>
          <p className="text-white font-bold text-2xl mb-6" data-id="element-463" data-mp="1ya67s15ifvie">
            {t.problem.closing2}
          </p>
          <p className="text-4xl font-extrabold text-gold" data-id="element-464" data-mp="3g5yld73j7jkk">
            {t.problem.closing3}
          </p>
        </motion.div>
      </div>
    </section>;
}